import { useState, useEffect, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { API } from "../App";

function ConfettiParticle({ delay, color }) {
  const left = Math.random() * 100;
  const size = 4 + Math.random() * 8;
  const dur = 2 + Math.random() * 2;
  return (
    <motion.div
      className="absolute pointer-events-none"
      style={{ left: `${left}%`, top: "-2%", width: `${size}px`, height: `${size * (0.5 + Math.random())}px`, backgroundColor: color, borderRadius: Math.random() > 0.5 ? "50%" : "2px" }}
      initial={{ y: 0, opacity: 1, rotate: 0 }}
      animate={{ y: "110vh", opacity: [1, 1, 0.8, 0], rotate: Math.random() * 720 - 360, x: (Math.random() - 0.5) * 200 }}
      transition={{ duration: dur, delay, ease: "easeIn" }}
    />
  );
}

function FireworkBurst({ x, y, delay }) {
  const colors = ["#D4AF37", "#F3E5AB", "#FFD700", "#FFF8DC", "#E8C547"];
  const particles = Array.from({ length: 16 }, (_, i) => {
    const angle = (i / 16) * Math.PI * 2;
    const dist = 60 + Math.random() * 80;
    return { dx: Math.cos(angle) * dist, dy: Math.sin(angle) * dist, color: colors[i % colors.length] };
  });
  return particles.map((p, i) => (
    <motion.div key={i} className="absolute rounded-full pointer-events-none"
      style={{ left: `${x}%`, top: `${y}%`, width: 4, height: 4, backgroundColor: p.color }}
      initial={{ scale: 0, x: 0, y: 0, opacity: 1 }}
      animate={{ scale: [0, 1.5, 0], x: p.dx, y: p.dy, opacity: [0, 1, 0] }}
      transition={{ duration: 1.2, delay: delay + i * 0.02, ease: "easeOut" }}
    />
  ));
}

export default function RaffleDisplayPage() {
  const { slug } = useParams();
  const [wedding, setWedding] = useState(null);
  const [drawData, setDrawData] = useState(null); // {draw, next_prize, total_prizes, drawn_prizes}
  const [winners, setWinners] = useState([]);
  const [phase, setPhase] = useState("idle"); // idle, rolling, winner, post-reveal, board
  const [cyclingName, setCyclingName] = useState("");
  const [audioReady, setAudioReady] = useState(false);
  const [needsAudioPrompt, setNeedsAudioPrompt] = useState(true);
  const audioRef = useRef(null);
  const cycleRef = useRef(null);
  const animTimer = useRef(null);
  const prevDrawId = useRef(null);
  const postRevealTimer = useRef(null);

  useEffect(() => {
    axios.get(`${API}/weddings/slug/${slug}`).then(r => setWedding(r.data)).catch(() => {});
  }, [slug]);

  // Preload audio
  useEffect(() => {
    const a = new Audio("/drumroll.wav");
    a.preload = "auto";
    audioRef.current = a;
    a.addEventListener("canplaythrough", () => setAudioReady(true));
  }, []);

  const enableAudio = () => {
    audioRef.current?.play().then(() => {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setNeedsAudioPrompt(false);
    }).catch(() => setNeedsAudioPrompt(true));
  };

  // Poll for active draw
  useEffect(() => {
    if (!wedding) return;
    const wid = wedding.wedding_id;
    const poll = async () => {
      try {
        const r = await axios.get(`${API}/raffle/${wid}/draws/active`);
        const data = r.data;
        setDrawData(data);

        const draw = data?.draw;
        if (!draw) {
          if (phase !== "rolling" && phase !== "board") {
            setPhase("idle");
          }
          return;
        }

        if (draw.status === "running" && draw.draw_id !== prevDrawId.current) {
          prevDrawId.current = draw.draw_id;
          startAnimation(draw);
        } else if ((draw.status === "revealed" || draw.status === "completed") && phase === "rolling") {
          // Animation was running, now reveal
          clearInterval(cycleRef.current);
          clearTimeout(animTimer.current);
          setCyclingName(draw.winner_name);
          setPhase("winner");
          audioRef.current?.pause();
        } else if ((draw.status === "revealed" || draw.status === "completed") && draw.draw_id !== prevDrawId.current) {
          prevDrawId.current = draw.draw_id;
          setCyclingName(draw.winner_name);
          setPhase("winner");
        }
      } catch {}
    };
    poll();
    const iv = setInterval(poll, 2000);
    return () => clearInterval(iv);
  }, [wedding, phase]);

  // Fetch winners for board
  useEffect(() => {
    if (!wedding) return;
    const fetchWinners = async () => {
      try {
        const r = await axios.get(`${API}/raffle/${wedding.wedding_id}/winners`);
        setWinners(r.data);
      } catch {}
    };
    fetchWinners();
    const iv = setInterval(fetchWinners, 5000);
    return () => clearInterval(iv);
  }, [wedding]);

  const startAnimation = useCallback((draw) => {
    setPhase("rolling");
    clearTimeout(postRevealTimer.current);
    if (audioRef.current && !needsAudioPrompt) {
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch(() => {});
    }
    const names = draw.pool_snapshot.map(p => p.display_name);
    const winner = draw.winner_name;
    const startTime = Date.now();
    const totalDuration = 10000;

    clearInterval(cycleRef.current);
    cycleRef.current = setInterval(() => {
      const progress = Math.min((Date.now() - startTime) / totalDuration, 1);
      if (progress < 0.7) {
        setCyclingName(names[Math.floor(Math.random() * names.length)]);
      } else if (progress < 0.9) {
        setCyclingName(Math.random() > 0.4 ? winner : names[Math.floor(Math.random() * names.length)]);
      } else {
        setCyclingName(winner);
      }
    }, 80);

    animTimer.current = setTimeout(() => {
      clearInterval(cycleRef.current);
      setCyclingName(winner);
      setPhase("winner");
      audioRef.current?.pause();
      audioRef.current && (audioRef.current.currentTime = 0);
    }, totalDuration);
  }, [needsAudioPrompt]);

  // Post-reveal: after 8 seconds on winner, transition to idle for next prize
  useEffect(() => {
    if (phase === "winner") {
      postRevealTimer.current = setTimeout(() => {
        setPhase("post-reveal");
        setTimeout(() => {
          prevDrawId.current = null;
          setPhase("idle");
        }, 3000);
      }, 8000);
    }
    return () => clearTimeout(postRevealTimer.current);
  }, [phase]);

  useEffect(() => {
    return () => { clearInterval(cycleRef.current); clearTimeout(animTimer.current); clearTimeout(postRevealTimer.current); audioRef.current?.pause(); };
  }, []);

  const showBoard = () => setPhase("board");
  const hideBoard = () => setPhase("idle");

  const confettiColors = ["#D4AF37", "#F3E5AB", "#FFD700", "#FFF8DC", "#EDEDED", "#E8C547", "#B8860B"];
  const fireworks = [{ x: 20, y: 30, delay: 0 }, { x: 80, y: 25, delay: 0.3 }, { x: 50, y: 20, delay: 0.6 }, { x: 15, y: 50, delay: 0.9 }, { x: 85, y: 45, delay: 1.2 }];

  if (!wedding) return <div className="min-h-screen bg-wedding-black flex items-center justify-center"><div className="w-16 h-16 border-2 border-wedding-gold border-t-transparent rounded-full animate-spin" /></div>;

  const coupleName = `${wedding.couple_name_1} & ${wedding.couple_name_2}`;
  const draw = drawData?.draw;
  const nextPrize = drawData?.next_prize;
  const totalPrizes = drawData?.total_prizes || 0;
  const drawnPrizes = drawData?.drawn_prizes || 0;

  return (
    <div className="min-h-screen bg-wedding-black overflow-hidden relative" data-testid="raffle-display-page">
      {/* Audio prompt */}
      {needsAudioPrompt && (
        <motion.div className="absolute inset-0 z-[100] bg-wedding-black flex items-center justify-center cursor-pointer" onClick={enableAudio} data-testid="audio-enable-overlay">
          <div className="text-center">
            <div className="w-24 h-24 rounded-full border-2 border-wedding-gold/40 flex items-center justify-center mx-auto mb-8">
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#D4AF37" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/><path d="M15.54 8.46a5 5 0 0 1 0 7.07"/><path d="M19.07 4.93a10 10 0 0 1 0 14.14"/></svg>
            </div>
            <p className="font-serif text-3xl text-wedding-ivory mb-3">Tap to Enable Audio</p>
            <p className="text-wedding-ivory/50 text-lg">Required for the drumroll experience</p>
          </div>
        </motion.div>
      )}

      <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse at 50% 40%, #1a1508 0%, #080604 50%, #020202 100%)" }} />

      {/* Ambient particles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {Array.from({ length: 20 }).map((_, i) => (
          <motion.div key={i} className="absolute w-1 h-1 rounded-full bg-wedding-gold/20"
            style={{ left: `${Math.random() * 100}%`, top: `${Math.random() * 100}%` }}
            animate={{ opacity: [0.1, 0.4, 0.1], scale: [0.5, 1, 0.5] }}
            transition={{ duration: 3 + Math.random() * 4, repeat: Infinity, delay: Math.random() * 3 }}
          />
        ))}
      </div>

      {/* Header */}
      <header className="absolute top-0 left-0 right-0 z-40 py-6 px-8">
        <div className="flex items-center justify-between">
          <p className="font-serif text-wedding-gold text-xl">{coupleName}</p>
          <div className="flex items-center gap-6">
            {totalPrizes > 0 && phase !== "board" && (
              <p className="text-wedding-ivory/30 text-sm tracking-widest">
                {drawnPrizes} / {totalPrizes} prizes
              </p>
            )}
            <p className="text-wedding-ivory/30 text-sm tracking-widest uppercase">Wedding Raffle</p>
          </div>
        </div>
      </header>

      {/* MAIN CONTENT */}
      <div className="relative z-10 flex flex-col items-center justify-center min-h-screen px-8">
        <AnimatePresence mode="wait">
          {/* IDLE */}
          {phase === "idle" && (
            <motion.div key="idle" className="text-center" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }}>
              {nextPrize ? (
                <>
                  <p className="text-wedding-gold text-sm tracking-[0.3em] uppercase mb-6">Next Prize</p>
                  <h1 className="font-serif text-5xl md:text-7xl text-wedding-ivory mb-4" data-testid="prize-display">{nextPrize.prize_name}</h1>
                  {nextPrize.prize_description && <p className="text-wedding-ivory/50 text-xl mt-2">{nextPrize.prize_description}</p>}
                  <div className="mt-12"><p className="text-wedding-ivory/30 text-lg tracking-widest uppercase animate-pulse">Waiting for draw...</p></div>
                </>
              ) : totalPrizes > 0 && drawnPrizes >= totalPrizes ? (
                // All prizes drawn — auto-show board
                <>
                  <div className="w-20 h-20 mx-auto mb-8 rounded-full border border-wedding-gold/20 flex items-center justify-center">
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#D4AF37" strokeWidth="1.5"><path d="M6 9H4.5a2.5 2.5 0 0 1 0-5C6 4 8 5.2 9 7.5M18 9h1.5a2.5 2.5 0 0 0 0-5C18 4 16 5.2 15 7.5M9 7.5h6M12 7.5V22M5 12v10h14V12"/></svg>
                  </div>
                  <h1 className="font-serif text-4xl md:text-6xl text-wedding-ivory mb-4">All Prizes Drawn</h1>
                  <p className="text-wedding-gold/60 text-xl mb-8">Congratulations to all our winners!</p>
                  <button onClick={showBoard} className="px-8 py-3 border border-wedding-gold/30 text-wedding-gold text-sm uppercase tracking-widest hover:bg-wedding-gold/5 transition-colors" data-testid="show-board-btn">
                    View Winners Board
                  </button>
                </>
              ) : (
                <>
                  <div className="w-20 h-20 mx-auto mb-8 rounded-full border border-wedding-gold/20 flex items-center justify-center">
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#D4AF37" strokeWidth="1.5"><path d="M20 12V22H4V12M22 7H2V12H22V7ZM12 22V7M12 7H7.5a2.5 2.5 0 0 1 0-5c1.5 0 3.3 1.2 4.5 5M12 7h4.5a2.5 2.5 0 0 0 0-5c-1.5 0-3.3 1.2-4.5 5"/></svg>
                  </div>
                  <h1 className="font-serif text-4xl md:text-6xl text-wedding-ivory mb-4">Wedding Raffle</h1>
                  <p className="text-wedding-ivory/40 text-xl">{coupleName}</p>
                  <p className="text-wedding-ivory/20 text-lg mt-8 tracking-widest uppercase">Ready</p>
                </>
              )}
            </motion.div>
          )}

          {/* ROLLING */}
          {phase === "rolling" && (
            <motion.div key="rolling" className="text-center" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              {draw?.prize_name && (
                <motion.p className="text-wedding-gold text-lg tracking-[0.3em] uppercase mb-8" animate={{ opacity: [0.5, 1, 0.5] }} transition={{ duration: 1.5, repeat: Infinity }}>
                  {draw.prize_name}
                </motion.p>
              )}
              <motion.div className="relative" animate={{ scale: [1, 1.02, 1] }} transition={{ duration: 0.3, repeat: Infinity }}>
                <div className="px-16 py-10 border-2 border-wedding-gold/40 bg-wedding-black/50 backdrop-blur-sm min-w-[500px]">
                  <AnimatePresence mode="wait">
                    <motion.p key={cyclingName} className="font-serif text-5xl md:text-7xl text-wedding-ivory whitespace-nowrap"
                      initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: -30, opacity: 0 }} transition={{ duration: 0.06 }}
                      data-testid="cycling-name">
                      {cyclingName}
                    </motion.p>
                  </AnimatePresence>
                </div>
                <div className="absolute -inset-2 border border-wedding-gold/10 pointer-events-none" />
              </motion.div>
              <p className="text-wedding-ivory/30 text-sm mt-8 tracking-widest uppercase">Selecting winner...</p>
            </motion.div>
          )}

          {/* WINNER */}
          {phase === "winner" && (
            <motion.div key="winner" className="text-center relative" initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} transition={{ type: "spring", stiffness: 200, damping: 15 }}>
              {draw?.prize_name && (
                <motion.p className="text-wedding-gold text-xl tracking-[0.3em] uppercase mb-4" initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
                  {draw.prize_name}
                </motion.p>
              )}
              <motion.p className="text-wedding-gold/60 text-lg uppercase tracking-widest mb-6" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }}>Winner</motion.p>
              <motion.h1 className="font-serif text-6xl md:text-8xl lg:text-9xl text-wedding-ivory" initial={{ scale: 0, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ delay: 0.2, type: "spring", stiffness: 150, damping: 12 }} data-testid="winner-name">
                {cyclingName}
              </motion.h1>
              <motion.div className="mt-8 w-32 h-0.5 bg-wedding-gold mx-auto" initial={{ scaleX: 0 }} animate={{ scaleX: 1 }} transition={{ delay: 0.8, duration: 0.6 }} />
            </motion.div>
          )}

          {/* POST-REVEAL (brief transition) */}
          {phase === "post-reveal" && (
            <motion.div key="post-reveal" className="text-center" initial={{ opacity: 1 }} animate={{ opacity: 0.3 }} transition={{ duration: 2.5 }}>
              <p className="text-wedding-gold text-sm tracking-[0.3em] uppercase mb-4">Winner</p>
              <h1 className="font-serif text-5xl text-wedding-ivory/60">{cyclingName}</h1>
              <p className="text-wedding-ivory/20 text-sm mt-6 tracking-widest uppercase">Preparing next round...</p>
            </motion.div>
          )}

          {/* WINNERS BOARD */}
          {phase === "board" && (
            <motion.div key="board" className="w-full max-w-4xl" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}>
              <div className="text-center mb-12">
                <p className="text-wedding-gold text-sm tracking-[0.3em] uppercase mb-4">All Winners</p>
                <h1 className="font-serif text-5xl md:text-6xl text-wedding-ivory" data-testid="board-title">Winners Board</h1>
              </div>
              <div className="space-y-4">
                {winners.map((w, i) => (
                  <motion.div key={w.draw_id}
                    className="flex items-center gap-6 px-8 py-5 bg-wedding-charcoal/50 border border-wedding-gold/10"
                    initial={{ opacity: 0, x: -30 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.1 }}
                  >
                    <span className="font-serif text-3xl text-wedding-gold/40 w-12 text-center">{i + 1}</span>
                    <div className="flex-1">
                      <p className="font-serif text-2xl md:text-3xl text-wedding-ivory">{w.winner_name}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-wedding-gold text-lg font-semibold">{w.prize_name}</p>
                      {w.prize_description && <p className="text-wedding-ivory/40 text-sm">{w.prize_description}</p>}
                    </div>
                  </motion.div>
                ))}
              </div>
              {winners.length === 0 && <p className="text-wedding-ivory/30 text-center text-lg py-12">No winners yet</p>}
              <div className="text-center mt-10">
                <button onClick={hideBoard} className="text-wedding-ivory/30 text-sm hover:text-wedding-ivory/60 transition-colors" data-testid="hide-board-btn">
                  Back to raffle
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* CONFETTI + FIREWORKS */}
      <AnimatePresence>
        {phase === "winner" && (
          <motion.div className="absolute inset-0 z-20 pointer-events-none overflow-hidden" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            {Array.from({ length: 80 }).map((_, i) => <ConfettiParticle key={i} delay={Math.random() * 1.5} color={confettiColors[i % confettiColors.length]} />)}
            {fireworks.map((fw, i) => <FireworkBurst key={i} x={fw.x} y={fw.y} delay={fw.delay} />)}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Bottom bar */}
      <div className="absolute bottom-0 left-0 right-0 z-30 py-4 px-8">
        <div className="flex items-center justify-between text-wedding-ivory/20 text-xs">
          <span>MemoraOrbs</span>
          <div className="flex items-center gap-6">
            {winners.length > 0 && phase !== "board" && (
              <button onClick={showBoard} className="hover:text-wedding-ivory/40 transition-colors" data-testid="board-toggle">
                Winners Board
              </button>
            )}
            {draw?.pool_count && <span>{draw.pool_count} participants</span>}
          </div>
        </div>
      </div>
    </div>
  );
}
